/**
 * Utilities for grouping coplanar faces.
 * @module
 */
import { BinaryTreeIterator, BinaryTreeNode } from "common/data/binaryTree";
import type { Face } from "geom/brep";
import { DIST_EPSILON } from "geom/core";
import Octree from "geom/octree";
import { planeIsOrientedCoplanar, planeIsOrientedCoplanarUvAligned, planeNormal } from "geom/plane";
import { vec3Dot } from "geom/vec3";
import type { Vec3 } from "geom/vec3";

export interface FaceWithMaterialKey extends Face {
  materialKey: string;
}

interface OrientedCoplanarFaceGroupContext {
  inputFaces: Face[];
  coplanarDistTolerance?: number;
  coplanarUvAngleTolerance?: number;
  coplanarFaceGroups?: Face[][];
  materialKey?: string;
}

interface PlaneFaceGroup {
  planeDistance: number;
  faces: Face[];
}

/**
 * A binary tree that stores the parallel face groups
 * sorted in groups by their distance from the origin
 */
type ParallelFaceSet = BinaryTreeNode<PlaneFaceGroup>;

function parallelFaceSetNew(tolerance: number) {
  return new BinaryTreeNode<PlaneFaceGroup>((n) => n.planeDistance, undefined, undefined, tolerance);
}

export class CoplanarFaceGrouper {
  /**
   * Plane normals are stored in this tree
   */
  private readonly tree = new Octree({
    minX: -2,
    minY: -2,
    minZ: -2,

    maxX: 2,
    maxY: 2,
    maxZ: 2,
  });

  /**
   * A map from the plane normals to parallel face set
   */
  private readonly planeVectorToFaceGroup = new Map<Vec3, ParallelFaceSet>();

  constructor(private readonly coplanarDistTolerance: number) {}

  /**
   * Insert the face into the tree
   */
  insert(face: Face) {
    // use the plane normal
    const [nx, ny, nz] = planeNormal(face.plane);
    const normal: Vec3 = [nx, ny, nz];

    // using the distance of the plane along the normal
    const planeDistance = vec3Dot(nx, ny, nz, face.plane.originX, face.plane.originY, face.plane.originZ);

    // first the get of parallel faces via the octree
    const res = this.tree.closestPointWithinDistance(normal, this.coplanarDistTolerance);

    // if there is not yet a set of parallel faces
    if (!res) {
      // insert the normal into the quadtree
      this.tree.insert(normal);

      // and create a new parallel face group
      const paragroup = parallelFaceSetNew(this.coplanarDistTolerance);
      paragroup.insert({ planeDistance, faces: [face] });

      // and store for later retrieval
      this.planeVectorToFaceGroup.set(normal, paragroup);
    } else {
      // if there is already a set of parallel faces, insert into the group
      const paragroup = this.planeVectorToFaceGroup.get(res.pt);
      if (paragroup) {
        const test = { planeDistance, faces: [face] };
        const g = paragroup.find(test);
        if (!g) {
          paragroup.insert({ planeDistance, faces: [face] });
        } else {
          g.faces.push(face);
        }
      }
    }
  }

  toFaceGroupArray() {
    const output: Face[][] = [];
    for (const group of this.planeVectorToFaceGroup) {
      group[1].forEach(BinaryTreeIterator.INCREASING, (v) => output.push(v.faces));
    }
    return output;
  }
}

export function gatherOrientedCoplanarFaceGroups(args: OrientedCoplanarFaceGroupContext): Face[][] {
  const { inputFaces, materialKey, coplanarUvAngleTolerance } = args;
  let { coplanarFaceGroups, coplanarDistTolerance } = args;

  if (coplanarFaceGroups === undefined) {
    coplanarFaceGroups = [];
  }
  if (!coplanarDistTolerance) {
    coplanarDistTolerance = DIST_EPSILON;
  }
  const normalAngleTolerance = DIST_EPSILON;

  // We group coplanar faces in an unefficient way by iterating over each face set and testing an input face with its first face
  for (const inputFace of inputFaces) {
    if (materialKey) {
      (inputFace as FaceWithMaterialKey).materialKey = materialKey;
    }

    let foundSet = false;
    for (const coplanarFaceGroup of coplanarFaceGroups) {
      const inputPlane = inputFace.plane;
      const groupPlane = coplanarFaceGroup[0].plane;

      const matchesFaceGroup =
        coplanarUvAngleTolerance !== undefined
          ? planeIsOrientedCoplanarUvAligned(
              inputPlane,
              groupPlane,
              coplanarDistTolerance,
              normalAngleTolerance,
              coplanarUvAngleTolerance,
            )
          : planeIsOrientedCoplanar(inputPlane, groupPlane, coplanarDistTolerance);
      if (matchesFaceGroup) {
        coplanarFaceGroup.push(inputFace);
        foundSet = true;
        break;
      }
    }

    if (!foundSet) {
      coplanarFaceGroups.push([inputFace]);
    }
  }

  return coplanarFaceGroups;
}

export function gatherOrientedCoplanarFaceGroupsFast(
  inputFaces: Face[] | undefined,
  coplanarDistTolerance: number = DIST_EPSILON,
  materialKey?: string,
): Face[][] {
  const tree = new CoplanarFaceGrouper(coplanarDistTolerance);

  if (inputFaces) {
    for (const inputFace of inputFaces) {
      if (materialKey) {
        (inputFace as FaceWithMaterialKey).materialKey = materialKey;
      }

      tree.insert(inputFace);
    }
  }

  return tree.toFaceGroupArray();
}
